// What every drawn model entity of a map shares (EntityRenderKit): the
// materials of its shaders, made with the map's shared shader uniforms, and
// the map's light grid for R_SetupEntityLighting; and the parts a model is
// made of (Part, MeshPart): a refEntity placed by its orientation whose vertex
// colors are lit like RB_CalcDiffuseColor. Players (game/playerModel.ts) and
// the map's static models (mapModels.ts) are built from them.

import * as THREE from 'three';
import { shaderKey } from './models/character';
import { meshTag, type MeshModel, type MeshSurface } from './models/md3';
import type { MdmSurface } from './models/mdm';
import type { Skeleton } from './models/skeleton';
import type { Orientation } from './models/skeleton';
import type { Skin } from './models/skin';
import { LIGHTMAP_NONE, createShaderMaterials, resolveShader, type SharedUniforms } from './q3shader';
import type { ShaderDef } from './shaderScripts';
import { colorShift, type TextureManager } from './textures';
import { lightGridOf, sampleLightGrid, type Bsp, type LightGrid } from '../shared/bsp';
import type { Vec3 } from '../shared/math';

const ENTITY_LIGHT_STEPS = 16;
/** Overbright shift of the map's light (mapRenderer LIGHT_SHIFT): R_ColorShiftLightingBytes on the grid. */
const LIGHT_SHIFT = 2;
/** r_ambientScale, r_directedScale */
const AMBIENT_SCALE = 0.5;
const DIRECTED_SCALE = 1;
/** Drawn after the world surfaces of the same sort. */
export const ENTITY_ORDER = 999000;
export const GHOST_ORDER = 9e8;
/** RB_CalcDiffuseColor's entityLightInt table (rgb 0..1), refilled for every part. */
const LIGHT_TABLE = new Float32Array(ENTITY_LIGHT_STEPS * 3);

export interface EntityLight {
  /** 0..255, clamped like R_SetupEntityLighting. */
  ambient: Vec3;
  directed: Vec3;
  /** World direction towards the light. */
  dir: Vec3;
}

export interface ShaderEntry {
  materials: THREE.RawShaderMaterial[];
  sort: number;
}

/** Weights texture width (texels); two texels per weight. */
const WEIGHTS_WIDTH = 1024;

export class EntityRenderKit {
  private readonly shaders = new Map<string, ShaderEntry>();
  /** Per MDM surface: its weights as a float texture (GPU skinning). */
  private readonly weightTextures = new Map<MdmSurface, THREE.DataTexture>();
  /** Draw skeletal surfaces skinned on the GPU (off: skinned on the CPU every frame). */
  gpuSkinning = true;
  private readonly animators: ((time: number) => void)[] = [];
  private readonly grid: LightGrid | null;
  readonly ghostMaterial = new THREE.MeshBasicMaterial({ color: 0x77ccff, vertexColors: true, transparent: true, opacity: 0.4, depthWrite: false });

  constructor(
    /** Shader definitions the models are drawn with. */
    private readonly shaderDefs: Map<string, ShaderDef>,
    private readonly textures: TextureManager,
    private readonly shared: SharedUniforms,
    bsp: Pick<Bsp, 'models' | 'lightGrid' | 'entityString'> | null,
  ) {
    const grid = bsp ? lightGridOf(bsp) : null;
    if (grid) {
      // R_LoadLightGrid: overbright shift of both colors of every point
      const data = grid.data.slice();
      for (let i = 0; i < data.length; i += 8) {
        colorShift(data, i, data, i, LIGHT_SHIFT);
        colorShift(data, i + 3, data, i + 3, LIGHT_SHIFT);
      }
      this.grid = { ...grid, data };
    } else this.grid = null;
  }

  /** Materials of a shader (R_FindShader with LIGHTMAP_NONE), created once per map; skinned: the GPU skinning variant. */
  shader(name: string, skinned = false): ShaderEntry {
    const key = shaderKey(name);
    const cacheKey = skinned ? `${key}#skinned` : key;
    let entry = this.shaders.get(cacheKey);
    if (!entry) {
      const shader = resolveShader(key, this.shaderDefs.get(key), LIGHTMAP_NONE);
      const materials = createShaderMaterials(shader, { textures: this.textures, shared: this.shared, lightmap: null, animators: this.animators, skinned });
      entry = { materials, sort: shader.sort };
      this.shaders.set(cacheKey, entry);
    }
    return entry;
  }

  /** A surface's bone weights, two texels each: (bone, weight, offset x, y), (offset z, 0, 0, 0). */
  weightTexture(surf: MdmSurface): THREE.DataTexture {
    let tex = this.weightTextures.get(surf);
    if (!tex) {
      const count = surf.weightValue.length;
      const rows = Math.max(1, Math.ceil((count * 2) / WEIGHTS_WIDTH));
      const data = new Float32Array(WEIGHTS_WIDTH * rows * 4);
      for (let w = 0; w < count; w++) {
        const o = w * 8;
        data[o] = surf.weightBone[w];
        data[o + 1] = surf.weightValue[w];
        data[o + 2] = surf.weightOffset[w * 3];
        data[o + 3] = surf.weightOffset[w * 3 + 1];
        data[o + 4] = surf.weightOffset[w * 3 + 2];
      }
      tex = new THREE.DataTexture(data, WEIGHTS_WIDTH, rows, THREE.RGBAFormat, THREE.FloatType);
      tex.magFilter = tex.minFilter = THREE.NearestFilter;
      tex.needsUpdate = true;
      this.weightTextures.set(surf, tex);
    }
    return tex;
  }

  /** R_SetupEntityLighting for a lighting origin seen from viewOrigin. */
  /** @param hilight light up far away models (players only, CG_Player) */
  light(lightOrigin: Vec3, viewOrigin: ArrayLike<number>, hilight = true): EntityLight {
    let ambient: Vec3;
    let directed: Vec3;
    let dir: Vec3;
    if (this.grid) {
      const g = sampleLightGrid(this.grid, lightOrigin);
      ambient = [g.ambient[0] * AMBIENT_SCALE, g.ambient[1] * AMBIENT_SCALE, g.ambient[2] * AMBIENT_SCALE];
      directed = [g.directed[0] * DIRECTED_SCALE, g.directed[1] * DIRECTED_SCALE, g.directed[2] * DIRECTED_SCALE];
      dir = g.dir;
    } else {
      ambient = [64, 64, 96];
      directed = [255, 232, 224];
      const l = Math.hypot(-1, 1, 1.25);
      dir = [-1 / l, 1 / l, 1.25 / l];
    }
    // far away players are lit up so they stay visible (CG_Player hilightIntensity / RF_MINLIGHT)
    const d2 = !hilight ? 0 : (lightOrigin[0] - viewOrigin[0]) ** 2 + (lightOrigin[1] - viewOrigin[1]) ** 2 + (lightOrigin[2] - viewOrigin[2]) ** 2;
    if (d2 > 384 * 384) {
      const over = d2 - 384 * 384;
      const hilight = over > 768 * 768 ? 1 : over / (768 * 768);
      const add = hilight ? 128 * hilight : 32;
      for (let k = 0; k < 3; k++) ambient[k] += add;
    }
    for (let k = 0; k < 3; k++) if (ambient[k] > 255) ambient[k] = 255;
    return { ambient, directed, dir };
  }

  /** Per frame shader work (animMap, noise waves); time in seconds like the map's. */
  update(time: number): void {
    for (const a of this.animators) a(time);
  }

  dispose(): void {
    for (const e of this.shaders.values()) for (const m of e.materials) m.dispose();
    this.shaders.clear();
    for (const t of this.weightTextures.values()) t.dispose();
    this.weightTextures.clear();
    this.ghostMaterial.dispose();
  }
}

// --- geometry ---

export interface SurfaceInstance {
  geometry: THREE.BufferGeometry;
  position: THREE.BufferAttribute;
  normal: THREE.BufferAttribute;
  color: THREE.BufferAttribute;
  numVerts: number;
}

/** A refEntity: a group placed by its orientation, with its surfaces. */
export class Part {
  readonly group = new THREE.Group();
  readonly surfaces: SurfaceInstance[] = [];
  orientation: Orientation = { origin: [0, 0, 0], axis: [[1, 0, 0], [0, 1, 0], [0, 0, 1]] };

  constructor(parent: THREE.Object3D) {
    this.group.matrixAutoUpdate = false;
    parent.add(this.group);
  }

  addSurface(kit: EntityRenderKit, shaderName: string, numVerts: number, indexes: Uint32Array, st: Float32Array, ghost: boolean, radius: number): SurfaceInstance {
    const geometry = new THREE.BufferGeometry();
    const dyn = (n: number) => new THREE.BufferAttribute(new Float32Array(numVerts * n), n).setUsage(THREE.DynamicDrawUsage);
    const position = dyn(3);
    const normal = dyn(3);
    const color = dyn(4);
    geometry.setAttribute('position', position);
    geometry.setAttribute('normal', normal);
    geometry.setAttribute('color', color);
    geometry.setAttribute('uv', new THREE.BufferAttribute(st, 2));
    geometry.setAttribute('lmuv', new THREE.BufferAttribute(new Float32Array(numVerts * 2), 2));
    // Quake triangles are wound clockwise: flip them to Three's counter-clockwise front
    const ccw = new Uint32Array(indexes.length);
    for (let i = 0; i + 2 < indexes.length; i += 3) {
      ccw[i] = indexes[i];
      ccw[i + 1] = indexes[i + 2];
      ccw[i + 2] = indexes[i + 1];
    }
    geometry.setIndex(new THREE.BufferAttribute(ccw, 1));
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), radius);
    if (ghost) {
      const mesh = new THREE.Mesh(geometry, kit.ghostMaterial);
      mesh.renderOrder = GHOST_ORDER;
      this.group.add(mesh);
    } else {
      const { materials, sort } = kit.shader(shaderName);
      materials.forEach((m, pass) => {
        const mesh = new THREE.Mesh(geometry, m);
        mesh.renderOrder = sort * 1e6 + ENTITY_ORDER + pass;
        this.group.add(mesh);
      });
    }
    const s: SurfaceInstance = { geometry, position, normal, color, numVerts };
    this.surfaces.push(s);
    return s;
  }

  /**
   * A skeletal (MDM) surface skinned on the GPU: static attributes (texture
   * coordinates, base normals, each vertex's first weight and weight count),
   * the bones and the light come in per draw from `skinning`.
   */
  addSkinnedSurface(kit: EntityRenderKit, shaderName: string, surf: MdmSurface, skinning: SkinningState, radius: number): void {
    const geometry = new THREE.BufferGeometry();
    const n = surf.numVerts;
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    geometry.setAttribute('normal', new THREE.BufferAttribute(surf.normals, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(n * 4), 4));
    geometry.setAttribute('uv', new THREE.BufferAttribute(surf.st, 2));
    geometry.setAttribute('lmuv', new THREE.BufferAttribute(new Float32Array(n * 2), 2));
    const skin = new Float32Array(n * 2);
    for (let v = 0; v < n; v++) {
      skin[v * 2] = surf.weightStart[v];
      skin[v * 2 + 1] = surf.weightCount[v];
    }
    geometry.setAttribute('skin', new THREE.BufferAttribute(skin, 2));
    const ccw = new Uint32Array(surf.indexes.length);
    for (let i = 0; i + 2 < surf.indexes.length; i += 3) {
      ccw[i] = surf.indexes[i];
      ccw[i + 1] = surf.indexes[i + 2];
      ccw[i + 2] = surf.indexes[i + 1];
    }
    geometry.setIndex(new THREE.BufferAttribute(ccw, 1));
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), radius);
    const weights = kit.weightTexture(surf);
    const { materials, sort } = kit.shader(shaderName, true);
    materials.forEach((m, pass) => {
      const mesh = new THREE.Mesh(geometry, m);
      mesh.renderOrder = sort * 1e6 + ENTITY_ORDER + pass;
      // the material is shared by every player: this model's bones and light go in right before its draw
      mesh.onBeforeRender = () => {
        const u = m.uniforms;
        u.uBones.value = skinning.bones;
        u.uWeights.value = weights;
        u.uWeightsWidth.value = weights.image.width;
        u.uLightAmbient.value = skinning.ambient;
        u.uLightDirected.value = skinning.directed;
        u.uLightDir.value = skinning.dir;
        m.uniformsNeedUpdate = true;
      };
      this.group.add(mesh);
    });
    this.surfaces.push({ geometry, position: geometry.getAttribute('position') as THREE.BufferAttribute, normal: geometry.getAttribute('normal') as THREE.BufferAttribute, color: geometry.getAttribute('color') as THREE.BufferAttribute, numVerts: 0 });
  }

  place(o: Orientation): void {
    this.orientation = o;
    const a = o.axis;
    this.group.matrix.set(a[0][0], a[1][0], a[2][0], o.origin[0], a[0][1], a[1][1], a[2][1], o.origin[1], a[0][2], a[1][2], a[2][2], o.origin[2], 0, 0, 0, 1);
    this.group.matrixWorldNeedsUpdate = true;
  }

  /** RB_CalcDiffuseColor: vertex colors from the normals (model space) and the light. */
  light(light: EntityLight): void {
    const ax = this.orientation.axis;
    // the light direction in model space (R_SetupEntityLighting)
    const lx = light.dir[0] * ax[0][0] + light.dir[1] * ax[0][1] + light.dir[2] * ax[0][2];
    const ly = light.dir[0] * ax[1][0] + light.dir[1] * ax[1][1] + light.dir[2] * ax[1][2];
    const lz = light.dir[0] * ax[2][0] + light.dir[1] * ax[2][1] + light.dir[2] * ax[2][2];
    const table = LIGHT_TABLE;
    for (let i = 0; i < ENTITY_LIGHT_STEPS; i++) {
      const d = i / (ENTITY_LIGHT_STEPS - 1);
      for (let k = 0; k < 3; k++) table[i * 3 + k] = Math.min(255, Math.trunc(light.ambient[k] + d * light.directed[k])) / 255;
    }
    // the 16 step dot product scaled once
    const sx = ENTITY_LIGHT_STEPS * lx;
    const sy = ENTITY_LIGHT_STEPS * ly;
    const sz = ENTITY_LIGHT_STEPS * lz;
    for (const s of this.surfaces) {
      const n = s.normal.array as Float32Array;
      const c = s.color.array as Float32Array;
      const count = s.numVerts;
      for (let v = 0, o3 = 0, o4 = 0; v < count; v++, o3 += 3, o4 += 4) {
        let dp = (n[o3] * sx + n[o3 + 1] * sy + n[o3 + 2] * sz) | 0;
        if (dp < 0) dp = 0;
        else if (dp >= ENTITY_LIGHT_STEPS) dp = ENTITY_LIGHT_STEPS - 1;
        const t = dp * 3;
        c[o4] = table[t];
        c[o4 + 1] = table[t + 1];
        c[o4 + 2] = table[t + 2];
        c[o4 + 3] = 1;
      }
      s.color.needsUpdate = true;
    }
  }

  dispose(): void {
    for (const s of this.surfaces) s.geometry.dispose();
    this.group.removeFromParent();
  }
}

/** What a GPU skinned model sends to its shaders each frame. */
export class SkinningState {
  /** Bone matrices: three texels per bone, rows of the matrix with the translation in w. */
  readonly bones: THREE.DataTexture;
  private readonly data: Float32Array;
  readonly ambient = new THREE.Vector3();
  readonly directed = new THREE.Vector3();
  /** The light direction in model space. */
  readonly dir = new THREE.Vector3();

  constructor(numBones: number) {
    this.data = new Float32Array(3 * Math.max(1, numBones) * 4);
    this.bones = new THREE.DataTexture(this.data, 3, Math.max(1, numBones), THREE.RGBAFormat, THREE.FloatType);
    this.bones.magFilter = this.bones.minFilter = THREE.NearestFilter;
  }

  /** The skeleton after Skeleton.calc(). */
  setBones(skeleton: Skeleton): void {
    const d = this.data;
    skeleton.bones.forEach((b, i) => {
      for (let r = 0; r < 3; r++) {
        const o = (i * 3 + r) * 4;
        d[o] = b.matrix[r][0];
        d[o + 1] = b.matrix[r][1];
        d[o + 2] = b.matrix[r][2];
        d[o + 3] = b.translation[r];
      }
    });
    this.bones.needsUpdate = true;
  }

  /** R_SetupEntityLighting for the model placed with `axis`. */
  setLight(light: EntityLight, axis: Orientation['axis']): void {
    this.ambient.set(light.ambient[0], light.ambient[1], light.ambient[2]);
    this.directed.set(light.directed[0], light.directed[1], light.directed[2]);
    const l = light.dir;
    this.dir.set(l[0] * axis[0][0] + l[1] * axis[0][1] + l[2] * axis[0][2], l[0] * axis[1][0] + l[1] * axis[1][1] + l[2] * axis[1][2], l[0] * axis[2][0] + l[1] * axis[2][1] + l[2] * axis[2][2]);
  }

  dispose(): void {
    this.bones.dispose();
  }
}

/** Shader of a surface: the skin's entry, else the one in the model file. */
export function surfaceShader(skin: Skin | null, name: string, fileShader: string): string {
  return skin?.surfaces.get(name.toLowerCase()) ?? fileShader;
}

/** A vertex model part (head, accessory, weapon). */
export class MeshPart extends Part {
  private frame = -1;
  private oldFrame = -1;
  private backlerp = -1;

  constructor(
    parent: THREE.Object3D,
    kit: EntityRenderKit,
    readonly model: MeshModel,
    skin: Skin | null,
    ghost: boolean,
    /** Bounding sphere radius of the surfaces (culling). */
    radius = 32,
  ) {
    super(parent);
    for (const s of model.surfaces) this.addSurface(kit, surfaceShader(skin, s.name, s.shaders[0] ?? ''), s.numVerts, s.indexes, s.st, ghost, radius);
    this.setFrame(0, 0, 0);
  }

  /** Positions and normals lerped between two frames (only when they change). */
  setFrame(frame: number, oldFrame: number, backlerp: number): void {
    const n = this.model.numFrames;
    frame = Math.min(Math.max(frame, 0), n - 1);
    oldFrame = Math.min(Math.max(oldFrame, 0), n - 1);
    if (frame === oldFrame) backlerp = 0;
    if (frame === this.frame && oldFrame === this.oldFrame && backlerp === this.backlerp) return;
    this.frame = frame;
    this.oldFrame = oldFrame;
    this.backlerp = backlerp;
    this.model.surfaces.forEach((s: MeshSurface, i) => {
      const inst = this.surfaces[i];
      const pos = inst.position.array as Float32Array;
      const nrm = inst.normal.array as Float32Array;
      const a = frame * s.numVerts * 3;
      const b = oldFrame * s.numVerts * 3;
      const front = 1 - backlerp;
      for (let k = 0; k < s.numVerts * 3; k++) {
        pos[k] = s.xyz[a + k] * front + s.xyz[b + k] * backlerp;
        nrm[k] = s.normals[a + k] * front + s.normals[b + k] * backlerp;
      }
      inst.position.needsUpdate = true;
      inst.normal.needsUpdate = true;
    });
  }

  tag(name: string): Orientation | null {
    return meshTag(this.model, name, this.frame, this.oldFrame, this.backlerp);
  }
}

